import { _TrueTypeReader } from './ttf-reader';
import { _PdfDictionary, _PdfName, Dictionary } from './../pdf-primitives';
import { _PdfFontMetrics } from './pdf-font-metrics';
import { _StandardWidthTable } from './pdf-font-metrics';
import { _FontDescriptorFlag } from '../enumerator';
import { _decode } from '../utils';
import { _PdfStream } from './../base-stream';
export class _UnicodeTrueTypeFont {
    constructor(data, size) {
        this._nameString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        this._isEmbedFont = false;
        this._cmapPrefix = '/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap' + '\r\n' + '/CIDSystemInfo << /Registry (Adobe)/Ordering (UCS)/Supplement 0>> def\n/CMapName ' + '/Adobe-Identity-UCS def\n/CMapType 2 def\n1 beginCodeSpacerange' + '\r\n';
        this._cmapEndCodeSpaceRange = 'endCodeSpacerange' + '\r\n';
        this._cmapBeginRange = 'beginbfrange' + '\r\n';
        this._cmapEndRange = 'endbfrange' + '\r\n';
        this._cmapSuffix = 'endbfrange\nendcmap\nCMapName currentdict ' + '/CMap defineresource pop\nend end' + '\r\n';
        if (data === null || typeof data === 'undefined') {
            throw new Error('ArgumentNullException:base64String');
        }
        this._fontSize = size;
        if (typeof data === 'string') {
            this._fontData = _decode(data);
        }
        else {
            this._fontData = data;
        }
        this._ttfReader = new _TrueTypeReader(this._fontData);
        this._ttfMetrics = this._ttfReader._metrics;
    }
    _beginSave() {
        this._descendantFontBeginSave();
        this._cmapBeginSave();
        this._fontDictionaryBeginSave();
        this._fontProgramBeginSave();
        if (this._fontDescriptor) {
            this._fontDescriptor.update('FontFile2', this._fontProgram);
            this._fontDescriptor._updated = true;
            this._fontDescriptor._isFont = true;
        }
    }
    _descendantFontBeginSave() {
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            let width = this._getDescendantWidth(); // eslint-disable-line
            if (width !== null) {
                this._descendantFont.set('W', width);
            }
        }
    }
    _fontDictionaryBeginSave() {
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            if (this._fontDictionary && !this._fontDictionary.has('ToUnicode')) {
                this._fontDictionary.update('ToUnicode', this._cmap);
            }
        }
    }
    _createInternals() {
        this._fontDictionary = new _PdfDictionary();
        this._descendantFont = new _PdfDictionary();
        this._metrics = new _PdfFontMetrics();
        this._ttfReader._createInternals();
        this._usedChars = null;
        const data = []; // eslint-disable-line
        this._fontProgram = new _PdfStream(data, new _PdfDictionary());
        this._cmap = new _PdfStream(data, new _PdfDictionary());
        this._ttfMetrics = this._ttfReader._metrics;
        this._initializeMetrics();
        this._subsetName = this._getFontName();
        this._createDescendantFont();
        this._createFontDictionary();
    }
    _getInternals() {
        return this._fontDictionary;
    }
    _initializeMetrics() {
        const ttfMetrics = this._ttfReader._metrics;
        this._metrics._ascent = ttfMetrics._macAscent;
        this._metrics._descent = ttfMetrics._macDescent;
        this._metrics._height = ttfMetrics._macAscent - ttfMetrics._macDescent + ttfMetrics._lineGap;
        this._metrics._name = ttfMetrics._fontFamily;
        this._metrics._postScriptName = ttfMetrics._postScriptName;
        this._metrics._size = this._fontSize;
        this._metrics._widthTable = new _StandardWidthTable(ttfMetrics._widthTable);
        this._metrics._lineGap = ttfMetrics._lineGap;
        this._metrics._subScriptSizeFactor = ttfMetrics._subScriptSizeFactor;
        this._metrics._superscriptSizeFactor = ttfMetrics._superscriptSizeFactor;
        this._metrics._isBold = ttfMetrics._isBold;
    }
    _getFontName() {
        let builder = '';
        for (let i = 0; i < 6; i++) {
            const index = Math.floor(Math.random() * (25 - 0 + 1)) + 0;
            builder += this._nameString[index];
        }
        builder += '+';
        builder += this._ttfReader._metrics._postScriptName;
        return builder.toString();
    }
    _createDescendantFont() {
        this._descendantFont = new _PdfDictionary();
        this._descendantFont._updated = true;
        this._descendantFont.set('Type', new _PdfName('Font'));
        this._descendantFont.set('Subtype', new _PdfName('CIDFontType2'));
        this._descendantFont.set('BaseFont', new _PdfName(this._subsetName));
        this._descendantFont.set('CIDToGIDMap', new _PdfName('Identity'));
        this._descendantFont.set('DW', 1000);
        this._fontDescriptor = this._createFontDescriptor();
        this._descendantFont.set('FontDescriptor', this._fontDescriptor);
        const systemInfo = this._createSystemInfo();
        this._descendantFont.set('CIDSystemInfo', systemInfo);
        this._descendantFont._isFont = true;
    }
    _createFontDescriptor() {
        const descriptor = new _PdfDictionary();
        const metrics = this._ttfReader._metrics;
        descriptor.set('Type', new _PdfName('FontDescriptor'));
        descriptor.set('FontName', new _PdfName(this._subsetName));
        descriptor.set('Flags', this._getDescriptorFlags());
        descriptor.set('FontBBox', this._getBoundBox());
        descriptor.set('MissingWidth', metrics._widthTable[32]);
        descriptor.set('StemV', metrics._stemV);
        descriptor.set('ItalicAngle', metrics._italicAngle);
        descriptor.set('CapHeight', metrics._capHeight);
        descriptor.set('Ascent', metrics._winAscent);
        descriptor.set('Descent', metrics._winDescent);
        descriptor.set('Leading', metrics._leading);
        descriptor.set('AvgWidth', metrics._widthTable[32]);
        descriptor.set('MaxWidth', metrics._widthTable[32]);
        descriptor.set('XHeight', 0);
        descriptor.set('StemH', 0);
        descriptor._updated = true;
        return descriptor;
    }
    _generateFontProgram() {
        let fontProgram = [];
        this._usedChars = (this._usedChars === null || typeof this._usedChars === 'undefined') ? new Dictionary()
            : this._usedChars;
        this._ttfReader._setOffset(0);
        fontProgram = this._ttfReader._readFontProgram(this._usedChars);
        this._fontProgram._clearStream();
        this._fontProgram._writeBytes(fontProgram);
    }
    _getBoundBox() {
        const rect = this._ttfReader._metrics._fontBox;
        const width = Math.abs(rect[2] - rect[0]);
        const height = Math.abs(rect[1] - rect[3]);
        const rectangle = [rect[0], rect[3], width, height];
        return rectangle;
    }
    _cmapBeginSave() {
        this._generateCmap();
    }
    _fontProgramBeginSave() {
        this._generateFontProgram();
    }
    _toHexString(n, isCaseChange) {
        let s = n.toString(16);
        if (isCaseChange) {
            s = s.toUpperCase();
        }
        return '<0000'.substring(0, 5 - s.length) + s + '>';
    }
    _generateCmap() {
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            const glyphChars = this._ttfReader._getGlyphChars(this._usedChars);
            if (glyphChars._size() > 0) {
                const keys = glyphChars.keys().sort();
                const first = keys[0];
                const last = keys[keys.length - 1];
                const middlePart = this._toHexString(first, false) + this._toHexString(last, false) + '\r\n';
                let builder = '';
                builder += this._cmapPrefix;
                builder += middlePart;
                builder += this._cmapEndCodeSpaceRange;
                let nextRange = 0;
                keys.forEach((key, index) => {
                    if (nextRange === 0) {
                        if (index !== 0) {
                            builder += this._cmapEndRange;
                        }
                        nextRange = Math.min(100, keys.length - index);
                        builder += nextRange;
                        builder += ' ';
                        builder += this._cmapBeginRange;
                    }
                    nextRange -= 1;
                    builder += this._toHexString(key, true) + this._toHexString(key, true);
                    builder += this._toHexString(glyphChars.getValue(key), true) + '\n';
                });
                builder += this._cmapSuffix;
                this._cmap._clearStream();
                this._cmap._write(builder);
            }
        }
    }
    _createFontDictionary() {
        this._fontDictionary._updated = true;
        this._fontDictionary.set('Type', _PdfName.get('Font'));
        this._fontDictionary.set('Subtype', _PdfName.get('Type0'));
        this._fontDictionary.set('BaseFont', new _PdfName(this._subsetName));
        this._fontDictionary.set('Encoding', _PdfName.get('Identity-H'));
        this._fontDictionary.set('DescendantFonts', this._descendantFont);
        this._fontDictionary._isFont = true;
        this._fontDictionary._currentObj = this;
    }
    _createSystemInfo() {
        const systemInfo = new _PdfDictionary();
        systemInfo._updated = true;
        systemInfo.set('Registry', 'Adobe');
        systemInfo.set('Ordering', 'Identity');
        systemInfo.set('Supplement', 0);
        return systemInfo;
    }
    _getDescriptorFlags() {
        let flags = 0;
        const metrics = this._ttfReader._metrics;
        if (metrics._isFixedPitch) {
            flags |= _FontDescriptorFlag.fixedPitch;
        }
        if (metrics._isSymbol) {
            flags |= _FontDescriptorFlag.symbolic;
        }
        else {
            flags |= _FontDescriptorFlag.nonSymbolic;
        }
        if (metrics._isItalic) {
            flags |= _FontDescriptorFlag.italic;
        }
        if (metrics._isBold) {
            flags |= _FontDescriptorFlag.forceBold;
        }
        return flags;
    }
    _getCharacterWidth(charCode) {
        const codeWidth = this._ttfReader._getCharacterWidth(charCode);
        return codeWidth;
    }
    _setSymbols(text) {
        if (text !== null && typeof text !== 'undefined') {
            if (this._usedChars === null || typeof this._usedChars === 'undefined') {
                this._usedChars = new Dictionary();
            }
            text.split('').forEach((ch) => {
                this._usedChars.setValue(ch, String.fromCharCode(0));
            });
        }
    }
    _getDescendantWidth() {
        let array = new Array(); // eslint-disable-line
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            const glyphInfo = [];
            const keys = this._usedChars.keys();
            keys.forEach((chLen) => {
                const glyph = this._ttfReader._getGlyph(chLen);
                glyphInfo.push(glyph);
            });
            glyphInfo.sort((a, b) => a._index - b._index);
            let firstGlyphIndex = 0;
            let lastGlyphIndex = 0;
            let firstGlyphIndexWasSet = false;
            let widthDetails = new Array(); // eslint-disable-line
            glyphInfo.forEach((glyph, index) => {
                if (!firstGlyphIndexWasSet) {
                    firstGlyphIndexWasSet = true;
                    firstGlyphIndex = glyph._index;
                    lastGlyphIndex = glyph._index - 1;
                }
                if ((lastGlyphIndex + 1 !== glyph._index || (index + 1 === glyphInfo.length)) && glyphInfo.length > 1) {
                    array.push(Number(firstGlyphIndex));
                    if (index !== 0) {
                        array.push(widthDetails);
                    }
                    firstGlyphIndex = glyph._index;
                    widthDetails = new Array(); // eslint-disable-line
                }
                widthDetails.push(Number(glyph._width));
                if ((index + 1) === glyphInfo.length) {
                    array.push(Number(firstGlyphIndex));
                    array.push(widthDetails);
                }
                lastGlyphIndex = glyph._index;
            });
        }
        return array;
    }
}
